/* JS Speed Shop: read-only, same-application vehicle matching. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!root.JSDieselFitment) root.JSDieselFitment = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  const keys = ['make', 'year', 'model', 'engine', 'type', 'body'];
  const params = Object.fromEntries(keys.map(key => [key, 'jsdf_' + key]));
  const catalogueParams = {
    make: 'filter.p.m.ymm.vehicle_make', model: 'filter.p.m.ymm.vehicle_model',
    year: 'filter.p.m.ymm.engine_years', engine: 'filter.p.m.ymm.engine_sizes',
    type: 'filter.p.m.ymm.engine_types'
  };
  const text = value => typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
  const normal = value => text(value).toLocaleLowerCase('en');
  const compare = (a, b) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
  // Catalogue facets flatten multi-make products. Keep known model names with
  // their make, while retaining the original Shopify values in URLs. This is
  // a model-name map, not an engine/year fitment table. Add new catalogue model
  // names here when extending the legacy vocabulary. Verified records are separate.
  const modelNames = {
    ford: ['F150', 'F250', 'F350', 'F450', 'F550', 'F650', 'F750', 'E-Series', 'Excursion'],
    gm: ['1500', '2500', '2500HD', '3500', '3500HD', '4500', '4500HD', '5500', '5500HD', '6500', '6500HD', 'Silverado', 'Sierra', 'Canyon', 'Colorado', 'Suburban', 'Savana'],
    ram: ['1500', '2500', '3500', '4500', '5500', 'Dodge'],
    jeep: ['Grand Cherokee', 'Gladiator', 'Wrangler'],
    mercedesbenz: ['Sprinter', 'Sprinter II', 'Sprinter III'],
    nissan: ['Titan', 'Titan XD']
  };
  const modelKey = value => normal(value).replace(/[^a-z0-9]/g, '');
  const makeAliases = { chevrolet: 'gm', chevy: 'gm', gmc: 'gm', generalmotors: 'gm', chevroletgmc: 'gm', dodge: 'ram', mercedes: 'mercedesbenz', benz: 'mercedesbenz' };
  function catalogueModelAllowed(make, model) {
    const makeKey = modelKey(make);
    const models = modelNames[makeAliases[makeKey] || makeKey];
    return !!models && models.some(value => modelKey(value) === modelKey(model));
  }
  // Known engine-family labels in this diesel catalogue. Separate product
  // lists do not establish a link between each tagged make and engine family.
  const engineFamilies = { ford: ['powerstroke'], gm: ['duramax'], ram: ['cummins', 'ecodiesel'], jeep: ['ecodiesel'], nissan: ['cummins'], mercedesbenz: [] };
  function catalogueTypeAllowed(make, type) {
    const makeKey = modelKey(make);
    const families = engineFamilies[makeAliases[makeKey] || makeKey] || [];
    return families.some(family => modelKey(type).startsWith(family));
  }
  function application(raw) {
    if (!raw || raw.verified !== true) return null;
    const a = Object.fromEntries(['make', 'model', 'engine', 'type', 'body', 'notes'].map(key => [key, text(raw[key])]));
    a.years = [...new Set((Array.isArray(raw.years) ? raw.years : []).map(text).filter(y => /^\d{4}(?:\.5)?$/.test(y)))];
    if (!a.make || !a.model || !a.engine || !a.years.length) return null;
    return a;
  }
  function index(products) {
    const rows = [];
    const seen = new Set();
    for (const product of products) {
      for (const variant of product.variants || []) {
        if (!/^\d+$/.test(String(variant.id || ''))) continue;
        for (const raw of variant.applications || []) {
          const app = application(raw);
          if (!app) continue;
          const id = JSON.stringify([product.id, variant.id, app]);
          if (seen.has(id)) continue;
          seen.add(id);
          rows.push({ product, variant, app });
        }
      }
    }
    return rows;
  }
  function matches(app, values) {
    return keys.every(key => {
      const expected = normal(values[key]);
      if (!expected) return true;
      return key === 'year' ? app.years.some(year => normal(year) === expected) : normal(app[key]) === expected;
    });
  }
  function matchingRows(rows, values) { return rows.filter(row => matches(row.app, values)); }
  function facets(rows, values, key) {
    // Each step is constrained by its completed parents, never its children.
    const without = Object.fromEntries(keys.slice(0, keys.indexOf(key)).map(k => [k, values[k]]));
    const eligible = matchingRows(rows, without);
    const options = new Map();
    for (const row of eligible) {
      const valuesForRow = key === 'year' ? row.app.years : [row.app[key]];
      for (const value of valuesForRow.filter(Boolean)) {
        const norm = normal(value);
        if (!options.has(norm)) options.set(norm, { value, label: value === 'GM' ? 'Chevrolet / GMC' : value, count: 0 });
        options.get(norm).count++;
      }
    }
    const list = [...options.values()].sort((a, b) => key === 'year' ? Number(b.value) - Number(a.value) : compare(a.label, b.label));
    return { options: list, total: eligible.length };
  }
  function results(rows, values) {
    const groups = new Map();
    for (const row of matchingRows(rows, values)) {
      const id = String(row.product.id);
      if (!groups.has(id)) groups.set(id, { product: row.product, variants: new Map() });
      const variants = groups.get(id).variants;
      const variantId = String(row.variant.id);
      if (!variants.has(variantId)) variants.set(variantId, { ...row.variant, matchedApplications: [] });
      variants.get(variantId).matchedApplications.push(row.app);
    }
    return [...groups.values()].map(group => ({ product: group.product, variants: [...group.variants.values()] }));
  }
  function readValues(url, mode) {
    const mapping = mode === 'applications' ? params : catalogueParams;
    return Object.fromEntries(keys.map(key => [key, mapping[key] ? text(url.searchParams.get(mapping[key])) : '']));
  }
  function urlFor(collectionURL, currentURL, values, mode, keepCollectionFilters = false) {
    const current = new URL(currentURL);
    const target = new URL(collectionURL, current);
    if (target.origin !== current.origin || !/\/collections\/[^/]+\/?$/.test(target.pathname)) throw new Error('Invalid collection URL');
    target.search = '';
    target.hash = '';
    if (keepCollectionFilters && mode === 'catalogue' && target.pathname === current.pathname) {
      for (const [name, value] of current.searchParams) {
        if (name === 'sort_by' || (name.startsWith('filter.') && !Object.values(catalogueParams).includes(name))) target.searchParams.append(name, value);
      }
    }
    if (current.searchParams.has('preview_theme_id')) target.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
    const mapping = mode === 'applications' ? params : catalogueParams;
    if (mode === 'applications') target.searchParams.set('view', 'js-diesel-fitment');
    for (const key of keys) if (text(values[key]) && mapping[key]) target.searchParams.set(mapping[key], text(values[key]));
    return target;
  }
  return { keys, params, catalogueParams, text, normal, compare, catalogueModelAllowed, catalogueTypeAllowed, application, index, matches, matchingRows, facets, results, readValues, urlFor };
});
